<template>

  <SidebarComponent :collapsible="isMobile ? 'offcanvas' : 'icon'" variant="floating">
    <SidebarHeader class="pt-2 px-0 gap-0 min-h-[128px]">
      <div
        class="flex"
        :class="{
          'flex-row items-center justify-between px-2 pb-2 border-b border-sidebar-border': isOpened,
          'flex-col items-center gap-2': !isOpened,
        }"
      >
        <div
          class="flex items-center"
          :class="{ 'flex-col gap-2': !isOpened, 'gap-1': isOpened }"
        >
          <div
            class="flex h-10 cursor-pointer items-center overflow-hidden pl-1.5"
            :class="isOpened ? 'w-[104px]' : 'w-8'"
            @click="handleLogoClick"
          >
            <LogoFull class="h-6 w-auto shrink-0" />
          </div>

          <DropdownMenu :modal="false" @update:open="handleNotificationsOpen">
            <DropdownMenuTrigger as-child>
              <button
                type="button"
                class="relative flex size-8 items-center justify-center rounded-none text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                :class="{
                  'opacity-0': triggerIsHidden,
                  'opacity-100': !triggerIsHidden,
                }"
                aria-label="Notifications"
              >
                <BellDot
                  class="size-4"
                  :class="{ 'animate-bell-shake': hasUnreadNotifications }"
                />
                <span
                  v-if="hasUnreadNotifications"
                  class="absolute right-1 top-1 flex size-2"
                >
                  <span class="absolute inset-0 inline-flex animate-ping rounded-full bg-new-red-500 opacity-75"></span>
                  <span class="relative inline-flex size-2 rounded-full bg-new-red-500"></span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              :side="isOpened ? 'bottom' : 'right'"
              class="w-[calc(100vw-40px)] p-0 md:w-96"
              data-cy="notifications-menu"
            >
              <div class="flex items-center justify-between border-b border-border bg-new-neutral-50 px-4 py-3">
                <p class="text-sm font-semibold text-foreground">Messages & events</p>
              </div>

              <div v-if="notificationsLoading" class="flex justify-center py-8">
                <Loader2Icon class="size-5 animate-spin text-muted-foreground" />
              </div>

              <ul v-else-if="notifications.length > 0" class="max-h-[480px] overflow-y-auto">
                <li
                  v-for="n in notifications"
                  :key="n.id"
                  class="flex gap-3 border-t border-border px-4 py-3 transition-colors first:border-t-0 hover:bg-muted/50"
                >
                  <span
                    class="mt-1.5 size-2 shrink-0 rounded-full"
                    :class="n.isRead ? 'border border-border bg-transparent' : 'bg-new-blue-500'"
                  ></span>
                  <div class="min-w-0 flex-1">
                    <span
                      class="inline-block px-2 py-[2px] text-[10px] min-[1920px]:text-[12px] font-medium capitalize text-foreground"
                      :class="{
                        'bg-new-blue-100': n.type === 'info',
                        'bg-new-neutral-200': n.type === 'report',
                        'bg-new-orange-200': n.type === 'warning',
                        'bg-new-red-200': n.type === 'critical',
                        'bg-new-violet-100': n.type === 'news',
                      }"
                    >
                      {{ n.type }}
                    </span>
                    <p
                      class="mt-1 text-sm font-semibold"
                      :class="n.isRead ? 'text-muted-foreground' : 'text-foreground'"
                    >
                      {{ n.title }}
                    </p>
                    <p
                      v-if="n.data"
                      class="mt-0.5 text-xs text-muted-foreground"
                      v-html="n.data"
                    ></p>
                    <a
                      v-if="n.button_text && n.button_url"
                      :href="n.button_url"
                      target="_blank"
                      class="mt-2 inline-block text-xs font-semibold text-new-blue-500 hover:underline"
                    >
                      {{ n.button_text }}
                    </a>
                    <p class="mt-1 text-[10px] min-[1920px]:text-[12px] text-muted-foreground">
                      {{ formatNotificationTime(n.created_at) }}
                    </p>
                  </div>
                </li>
              </ul>

              <div v-else class="px-4 py-8 text-center">
                <p class="text-sm text-muted-foreground">You have no new messages.</p>
              </div>

              <button
                v-if="canFetchMoreNotifications && notifications.length > 0 && !notificationsLoading"
                type="button"
                class="w-full border-t border-border bg-new-neutral-50 py-3 text-xs font-medium text-new-blue-500 transition-colors hover:bg-new-neutral-100"
                @click="loadMoreNotifications"
              >
                Show older
              </button>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <SidebarTrigger class="size-8 rounded-none">
          <ArrowRightToLine
            class="size-4 "
            :class="{
              'rotate-180': isOpened,
              'opacity-0': triggerIsHidden,
              'opacity-100': !triggerIsHidden,
            }"
          />
        </SidebarTrigger>
      </div>
    </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem v-for="item in links" :key="item.id">
                <SidebarMenuButton variant="outline"  as-child :is-active="page.startsWith(item.id)" >
                    <inertia-link :href="item.href">
                      <component :is="item.icon" />

                        <span
                          class="whitespace-nowrap transition-opacity ease-in"
                          :class="isOpened ? 'opacity-100 duration-500 delay-200' : 'opacity-0 duration-100 delay-0'"
                        >
                          {{ item.name }}
                        </span>

                      <SidebarMenuBadge
                        class="flex! transition-opacity ease-in"
                        :class="isOpened ? 'opacity-100 duration-500 delay-200' : 'opacity-0 duration-100 delay-0'"
                      >{{ item.count }}</SidebarMenuBadge>

                    </inertia-link>

                    <SidebarMenuSub v-if="open && !isMobile && page.startsWith(item.id) && item.children">
                      <SidebarMenuSubItem v-for="child in item.children" :key="child.id" >
                        <SidebarMenuSubButton as-child size="sm" :is-active="page === child.id">
                          <inertia-link :href="child.href">
                            <span
                              class="whitespace-nowrap transition-opacity ease-in"
                              :class="isOpened ? 'opacity-100 duration-500 delay-200' : 'opacity-0 duration-100 delay-0'"
                            >
                              {{ child.name }}
                            </span>
                          </inertia-link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                </SidebarMenuButton>
              </SidebarMenuItem>
          </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

    <SidebarFooter class="p-2 mt-1">
      <SidebarTeamSwitch />
    </SidebarFooter>

  </SidebarComponent>
</template>

<script lang="ts" setup>


import {
  SidebarComponent,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import sidebar from "@/data/sidebar";
import sidebarNew from "@/data/sidebar-new";
import pluginSidebar from "@/data/pluginSidebar-new";
import { ArrowRightToLine, BellDot, Loader2Icon } from "lucide-vue-next";
import { ILink } from "@/types/Atomic/ISidebarData";
import { ITopolPageProps } from "@/types/TopolPageProps/TopolPageProps";
import IAnnouncement from "@/types/Announcements/IAnnouncement";
import { formatDistance } from "date-fns";



const { isMobile, openMobile, open, setOpenMobile } = useSidebar();

let removeNavigateListener: (() => void) | null = null;

onMounted(() => {
    removeNavigateListener = router.on('navigate', () => {
        if (isMobile.value) {
            setOpenMobile(false);
        }
    });
});

onUnmounted(() => {
    removeNavigateListener?.();
});
const { links: kioskLinks } = sidebar();
const { links: appLinks } = sidebarNew();
const { links: pluginLinks } = pluginSidebar();

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const page = computed(() => usePage<ITopolPageProps>().url);

const isKiosk = computed(() => {
    return page.value.startsWith('/kiosk');
});

// Computed (not a ref+onMounted snapshot) so the badge counts/labels stay reactive
// when underlying data (e.g. membersCount) updates after the page has mounted.
const links = computed<ILink[]>(() => {
    if (isKiosk.value) return kioskLinks.value;
    const appType = globalProps.value.user.app_type;
    if (appType === 'plugin') return pluginLinks.value;
    if (appType === 'pro' || appType === 'both' || appType === null) return appLinks.value;
    return [];
});

const isOpened = computed(() => {
  return (open.value && !isMobile.value) || (openMobile.value && isMobile.value);
});

const handleLogoClick = () => {
  if (isMobile.value) {
    openMobile.value = false;
  }

  router.visit('/');
};

const hasUnreadNotifications = ref(false);
const notificationsLoading = ref(false);
const notificationsLoaded = ref(false);

const {
  data: notifications,
  fetchData: fetchNotifications,
  currentPage: notificationsPage,
  canFetchMore: canFetchMoreNotifications,
} = useResource<IAnnouncement>('/announcements-notifications', {
  perPage: 10,
  hasInfiniteScroll: true,
});

const fetchUnreadStatus = async () => {
  try {
    const response = await axios.get(route('user.has-unread-announcements.index'));
    if (response.data.success) {
      hasUnreadNotifications.value = response.data.data;
    }
  } catch {
    // silently ignore — bell still works without the badge
  }
};

const markAllRead = async () => {
  try {
    await axios.patch(route('announcements-notifications.update'));
  } finally {
    hasUnreadNotifications.value = false;
  }
};

const handleNotificationsOpen = async (open: boolean) => {
  if (!open) return;
  if (!notificationsLoaded.value) {
    notificationsLoading.value = true;
    try {
      await fetchNotifications();
      notificationsLoaded.value = true;
    } finally {
      notificationsLoading.value = false;
    }
  }
  if (hasUnreadNotifications.value) {
    await markAllRead();
  }
};

const loadMoreNotifications = async () => {
  notificationsPage.value += 1;
  await fetchNotifications();
};

const formatNotificationTime = (createdAt: string): string => {
  return formatDistance(new Date(createdAt), new Date(), { addSuffix: true });
};

onMounted(() => {
  fetchUnreadStatus();
});

const triggerIsHidden = ref(false)

watch(
  () => isOpened.value,
  async () => {
    // Hide
    triggerIsHidden.value = true

    // Wait 400ms
    await new Promise(resolve => setTimeout(resolve, 400))

    // Show again (now rotated already)
    triggerIsHidden.value = false
  }
)

</script>

<style scoped>
.fade-enter-active {
  transition: opacity 1s;
}

.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

@keyframes bell-shake {
  0%, 80%, 100% { transform: rotate(0deg); }
  84% { transform: rotate(-12deg); }
  88% { transform: rotate(10deg); }
  92% { transform: rotate(-6deg); }
  96% { transform: rotate(4deg); }
}

.animate-bell-shake {
  transform-origin: top center;
  animation: bell-shake 4s ease-in-out infinite;
}
</style>
